/**
 * DegreeDaysAnalyticsSection — HDD65/CDD65 on the Other tab.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import DegreeDaysCharts from '../charts/DegreeDaysCharts';
import { LATEST_INVENTORY_YEAR } from '../../services/capTargetsProgressService';
import {
  fetchDegreeDaySeries,
  getDegreeDayRowForYear,
  resolveClimateDisplayYear,
  resolveDegreeDayLocation,
  type DegreeDaySeries,
} from '../../services/degreeDaysDataService';
import { generateDegreeDayOverviewNarrative } from '../../services/degreeDaysNarrativeService';

interface DegreeDaysAnalyticsSectionProps {
  location: string;
}

const DegreeDaysAnalyticsSection: React.FC<DegreeDaysAnalyticsSectionProps> = ({ location }) => {
  const [activeNarrativeView, setActiveNarrativeView] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unsupported, setUnsupported] = useState<string | null>(null);
  const [series, setSeries] = useState<DegreeDaySeries | null>(null);
  const [displayName, setDisplayName] = useState(location);

  const { year: climateYear, substituted } = useMemo(
    () => resolveClimateDisplayYear(LATEST_INVENTORY_YEAR),
    []
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      setUnsupported(null);
      setSeries(null);
      try {
        const ctx = await resolveDegreeDayLocation(location);
        if (cancelled) return;
        if (!ctx.supported || !ctx.prismGeofips) {
          setUnsupported(ctx.unsupportedReason ?? 'This geography is not supported for degree days.');
          return;
        }
        setDisplayName(ctx.prismLocationName ?? location);
        const data = await fetchDegreeDaySeries(ctx.prismGeofips);
        if (!cancelled) setSeries(data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load degree day data.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [location]);

  const { city: yearRow, statewide: stateRow } = useMemo(
    () => (series ? getDegreeDayRowForYear(series, climateYear) : { city: null, statewide: null }),
    [series, climateYear]
  );

  const narrative = useMemo(
    () =>
      generateDegreeDayOverviewNarrative({
        locationLabel: displayName,
        year: climateYear,
        yearSubstituted: substituted,
        city: yearRow,
        statewide: stateRow,
      }),
    [displayName, climateYear, substituted, yearRow, stateRow]
  );

  const diffLabel = (actual: number | undefined, benchmark: number | undefined) => {
    if (actual == null || benchmark == null) return '—';
    const diff = Math.round(actual - benchmark);
    const sign = diff > 0 ? '+' : '';
    return `${sign}${diff.toLocaleString()}`;
  };

  const keyMetrics = (
    <KeyMetrics>
      <KeyMetric
        value={loading ? '…' : yearRow ? Math.round(yearRow.hdd65_actual).toLocaleString() : '—'}
        label={`HDD65 actual (${climateYear}), degree days base 65°F`}
        color="#2563eb"
      />
      <KeyMetric
        value={loading ? '…' : yearRow ? Math.round(yearRow.cdd65_actual).toLocaleString() : '—'}
        label={`CDD65 actual (${climateYear}), degree days base 65°F`}
        color="#f59e0b"
      />
      <KeyMetric
        value={
          loading
            ? '…'
            : diffLabel(yearRow?.hdd65_actual, stateRow?.mean_hdd65_actual)
        }
        label="HDD vs CA incorporated-city avg"
        color="#64748b"
      />
      <KeyMetric
        value={
          loading
            ? '…'
            : diffLabel(yearRow?.cdd65_actual, stateRow?.mean_cdd65_actual)
        }
        label="CDD vs CA incorporated-city avg"
        color="#64748b"
      />
      <KeyMetric
        value={
          loading
            ? '…'
            : yearRow
              ? `${yearRow.hdd65_anomaly >= 0 ? '+' : ''}${Math.round(yearRow.hdd65_anomaly).toLocaleString()}`
              : '—'
        }
        label="HDD vs 1991–2020 normal"
        color="#0ea5e9"
      />
      <KeyMetric
        value={
          loading
            ? '…'
            : yearRow
              ? `${yearRow.cdd65_anomaly >= 0 ? '+' : ''}${Math.round(yearRow.cdd65_anomaly).toLocaleString()}`
              : '—'
        }
        label="CDD vs 1991–2020 normal"
        color="#0ea5e9"
      />
    </KeyMetrics>
  );

  if (unsupported) {
    return (
      <ChartLayout
        title="Heating and Cooling Degree Days"
        description="Annual heating and cooling demand indicators derived from daily PRISM temperature data."
        chartType="line"
        chartContent={
          <div style={{ padding: 32, color: '#374151', fontSize: 14, lineHeight: 1.6 }}>
            <p>{unsupported}</p>
            <p style={{ marginTop: 12, color: '#6b7280' }}>
              This phase provides city-point HDD65 and CDD65 for 482 Census 2020 incorporated California
              places. County, regional, and statewide location selections will be supported in a future
              release.
            </p>
          </div>
        }
        overviewContent={
          <OverviewContent title="Overview" description="Geography not supported for this dataset.">
            <p>{unsupported}</p>
          </OverviewContent>
        }
        activeView={activeNarrativeView}
        onViewChange={setActiveNarrativeView}
        showRelevanceEquity={false}
        location={location}
        appendLocationToTitle={false}
      />
    );
  }

  return (
    <ChartLayout
      title="Heating and Cooling Degree Days"
      description="Annual heating and cooling demand indicators derived from daily PRISM temperature data."
      chartType="line"
      keyMetrics={keyMetrics}
      chartContent={
        loading ? (
          <div style={{ padding: 40, color: '#6b7280', textAlign: 'center' }}>Loading degree day data…</div>
        ) : error ? (
          <div style={{ padding: 40, color: '#b91c1c', textAlign: 'center' }}>{error}</div>
        ) : series ? (
          <DegreeDaysCharts
            citySeries={series.city}
            statewideSeries={series.statewide}
            normalHdd65={series.normalHdd65}
            normalCdd65={series.normalCdd65}
            selectedYear={climateYear}
            locationLabel={displayName}
          />
        ) : (
          <div style={{ padding: 40, color: '#6b7280', textAlign: 'center' }}>No data available.</div>
        )
      }
      overviewContent={
        <OverviewContent
          title="Overview"
          description="Heating and cooling degree days summarize how outdoor temperatures differ from a 65°F reference temperature over the course of a year."
        >
          <p>{narrative}</p>
          <p style={{ marginTop: 12 }}>
            Heating and cooling degree days accumulate when daily mean temperature is below or above 65°F,
            respectively. Higher values indicate greater weather-related potential demand for space heating or
            cooling, but they do not directly measure energy consumption.
          </p>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to climate planning"
          description="Degree days help cities distinguish changes caused by weather from changes caused by building efficiency, fuel choice, behavior, or energy policy."
        >
          <p>
            Actual-year degree days support annual greenhouse gas inventories, while comparison with
            1991–2020 normals supports weather-normalized benchmarking. Rising cooling degree days can also
            indicate growing electricity demand, peak-load pressure, and the need for heat-resilient
            buildings. Degree days alone do not predict exact energy demand.
          </p>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity considerations"
          description="The effects of heating and cooling demand are not evenly distributed."
        >
          <p>
            Lower-income households, renters, residents of older or poorly insulated buildings, and households
            without efficient cooling can face greater energy costs and health risks during hot or cold
            conditions. Citywide degree days describe general climate exposure but do not capture neighborhood
            heat islands, housing quality, access to air conditioning, energy insecurity, or differences in
            household vulnerability.
          </p>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Methods and limitations"
          description="ClimatePlans calculates HDD65 and CDD65 from daily PRISM mean temperature at the Census internal point for each incorporated place."
          dataSources={[
            'PRISM Climate Group — daily mean temperature, 800 m stable product, 2000–2024',
            'PRISM Climate Group — 1991–2020 daily temperature normals',
            'U.S. Census Bureau — 2020 incorporated-place internal points (INTPTLAT/INTPTLON)',
          ]}
          methodology={[
            'HDD65 = max(65°F − daily mean temperature, 0)',
            'CDD65 = max(daily mean temperature − 65°F, 0)',
            'Annual totals sum daily degree days for each calendar year',
            'Leap years use 366-day normal totals; non-leap years use 365-day normal totals',
            'February 29 is included in PRISM 1991–2020 daily normals and excluded from 365-day normal totals',
            'California incorporated-city average is the arithmetic mean of 482 Census 2020 incorporated-place representative points',
            'ClimatePlans registry geofips are mapped to PRISM incorporated-place geofips via city_climate_geography_crosswalk',
            'Legacy CDP registry entries for Greenfield, Live Oak, and Mountain View map to incorporated-place GEOIDs',
          ]}
        />
      }
      activeView={activeNarrativeView}
      onViewChange={setActiveNarrativeView}
      showRelevanceEquity={true}
      enableComments={true}
      location={location}
      appendLocationToTitle={true}
    />
  );
};

export default DegreeDaysAnalyticsSection;
